/**
 * MOLR cart-drawer enhancements (module)
 * --------------------------------------
 * One progressive enhancement, a no-op without JS: the cart drawer's cross-sell
 * "Add" (snippets/molr-cart-cross-sell.liquid) is a real Shopify add-to-cart
 * form — with no JS it POSTs to /cart/add and lands on the cart page. Here we
 * intercept it and add over AJAX instead, reusing Horizon's own
 * CartLinesUpdateEvent so the drawer + header count re-render in place, exactly
 * the path product-form.js and molr-product.js use. Any failure falls back to a
 * native form submit, so the add can never be lost.
 *
 * Bound by delegation so it survives the drawer's Section-Rendering re-renders.
 * Skipped in the theme editor (design mode re-renders sections and fights state).
 * The free-shipping progress bar needs no JS — it is pure Liquid + CSS (§33),
 * recomputed server-side on every cart mutation.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  /** Section ids of every cart-items component, so the add response carries the
   *  re-rendered drawer markup Horizon needs to morph. */
  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  async function ajaxAdd(form, button) {
    const idInput = form.querySelector('input[name="id"]');
    const qtyInput = form.querySelector('input[name="quantity"]');
    const id = idInput && idInput.value;
    const quantity = parseInt(qtyInput && qtyInput.value, 10) || 1;
    if (!id) throw new Error('No variant to add');

    // Optimistic event — lets any listener react before the round-trip lands.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: id, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const addRes = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id, quantity }],
          sections: cartSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const added = await addRes.json();
      if (added.status || added.status === 422) {
        deferred.reject(added);
        throw new Error(added.description || added.message || 'Add to cart failed');
      }

      const cartRes = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'molr-cross-sell',
          sections: added.sections,
          didError: false,
        },
      });

      if (button) confirmButton(button);
      return true;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  /** Flash the "Add" into a confirmed state briefly (the row itself is about to
   *  be re-rendered away by the drawer morph, so this is a graceful touch, not
   *  load-bearing). */
  function confirmButton(button) {
    button.classList.add('is-added');
    button.disabled = true;
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.disabled = false;
    }, 1600);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target.closest('form[data-molr-cross-sell]') : null;
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[data-molr-add-btn]');
    ajaxAdd(form, button).catch(() => {
      // Offline / unexpected response: honour the add by letting the real form
      // post, so the item is never silently lost.
      form.submit();
    });
  });
})();
